import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Building2, MapPin, Layers, Pencil, Trash2, Search, Briefcase, ArrowRight, Rows3, LayoutGrid } from "lucide-react";
import Layout from "@/components/app/Layout";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCachedGet } from "@/lib/cache";
import { COUNTRY_BY_CODE } from "@/lib/countries";
import ClientDialog, { hiringLabel } from "@/components/app/clients/ClientDialog";
import { can } from "@/lib/perm";
import ClientsList from "@/components/app/clients/ClientsList";

const VIEW_KEY = "rs_clients_view";


function ClientCard({ c, canEdit, onEdit, onDelete }) {
  const country = COUNTRY_BY_CODE[c.country_code];
  return (
    <div className="tp-card p-5 flex flex-col group" data-testid={`client-card-${c.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center font-display font-semibold shrink-0">{c.name[0]?.toUpperCase()}</div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-semibold truncate" data-testid={`client-name-${c.id}`}>{c.name}</div>
          <div className="text-xs text-slate-500">{country ? `${country.flag || ""} ${country.name}` : "Country not set"}</div>
        </div>
        {canEdit && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button data-testid={`client-edit-${c.id}`} onClick={onEdit} className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Edit"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`client-delete-${c.id}`} onClick={onDelete} className="p-1.5 rounded text-slate-400 hover:text-rose-500 hover:bg-rose-50" title="Delete"><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {c.hiring_types.map(t => <span key={t} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{hiringLabel(t)}</span>)}
      </div>
      <div className="space-y-3 text-sm flex-1">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> Accounts ({c.accounts.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {c.accounts.length === 0 && <span className="text-xs text-slate-400">None</span>}
            {c.accounts.map(a => (
              <span key={a.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700">
                {a.name}{a.location && <><MapPin className="w-3 h-3 text-slate-400" />{a.location}</>}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Layers className="w-3 h-3" /> Service Lines ({c.service_lines.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {c.service_lines.length === 0 && <span className="text-xs text-slate-400">None</span>}
            {c.service_lines.map(s => <span key={s.id} className="text-xs px-2 py-1 rounded-md bg-indigo-50 text-indigo-700">{s.name}</span>)}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-[color:var(--bd-sub)] text-xs text-slate-500 flex items-center justify-between gap-1.5">
        <span className="flex items-center gap-1.5" data-testid={`client-jobs-${c.id}`}><Briefcase className="w-3.5 h-3.5" /> {c.job_count || 0} job{c.job_count === 1 ? "" : "s"}</span>
        <Link to={`/app/clients/${c.id}`} data-testid={`client-dashboard-link-${c.id}`} className="inline-flex items-center gap-1 font-medium text-indigo-600 hover:text-indigo-500">View dashboard <ArrowRight className="w-3.5 h-3.5" /></Link>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const { user } = useAuth();
  const canEdit = can(user, "clients.manage");
  const [clients, reload] = useCachedGet("/clients");
  const [dialog, setDialog] = useState(null); // null | "new" | client
  const [q, setQ] = useState("");
  const [view, setViewState] = useState(() => localStorage.getItem(VIEW_KEY) || "grid");
  const setView = (v) => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch (_) {} };
  const list = (clients || []).filter(c => {
    const s = q.trim().toLowerCase();
    return !s || [c.name, ...c.accounts.map(a => a.name), ...c.service_lines.map(x => x.name)].some(v => v.toLowerCase().includes(s));
  });
  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); reload(); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };
  return (
    <Layout actions={canEdit && (
      <button data-testid="new-client-btn" onClick={() => setDialog("new")} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
        <Plus className="w-4 h-4" /> Onboard Client
      </button>
    )}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Organisation</div>
          <h1 className="font-display text-3xl font-bold">Clients</h1>
          <div className="text-sm text-slate-500 mt-1">Onboard clients with their accounts and service lines, then tag every job to a client.</div>
        </div>
        {clients?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="inline-flex rounded-lg border border-[color:var(--bd-sub)] bg-[color:var(--bg-card)] p-0.5" data-testid="client-view-toggle">
            {[["list", Rows3, "Rows"], ["grid", LayoutGrid, "Grid"]].map(([k, Icon, label]) => (
              <button key={k} data-testid={`client-view-${k}`} aria-pressed={view === k} onClick={() => setView(k)}
                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${view === k ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 sm:flex-none sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients, accounts, service lines"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
          </div>
          </div>
        )}
      </div>
      {clients === undefined ? null : clients.length === 0 ? (
        <div className="tp-card p-12 text-center" data-testid="clients-empty">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg mb-1">No clients yet</div>
          <div className="text-sm text-slate-500 mb-4">{canEdit ? "Onboard your first client — including your own company for internal roles." : "Ask your Tenant Admin or TAG Head to onboard clients."}</div>
          {canEdit && <button onClick={() => setDialog("new")} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white"><Plus className="w-4 h-4" /> Onboard Client</button>}
        </div>
      ) : view === "list" ? (
        list.length ? <ClientsList clients={list} canEdit={canEdit} onEdit={(c) => setDialog(c)} onDelete={remove} />
          : <div className="text-sm text-slate-500" data-testid="clients-no-match">No clients match “{q}”.</div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="clients-grid">
          {list.map(c => <ClientCard key={c.id} c={c} canEdit={canEdit} onEdit={() => setDialog(c)} onDelete={() => remove(c)} />)}
          {list.length === 0 && <div className="text-sm text-slate-500">No clients match “{q}”.</div>}
        </div>
      )}
      {dialog && <ClientDialog client={dialog === "new" ? null : dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); reload(); }} />}
    </Layout>
  );
}
